import { DocumentNode, gql, useQuery } from '@apollo/client';
import { Heading, Stack, VStack } from '@chakra-ui/react';
import { ContentState, EditorState, convertFromRaw } from 'draft-js';
import * as React from 'react';

import { InfoCard } from '@/components/InfoCard';
import { OrganizationProfileImage } from '@/components/OrganizationProfileImage';
import { Redraft } from '@/components/Redraft';
import { Resources } from '@/components/Resources';
import { MessageModal } from '@/components/organizations/Detail/components/MessageModal';
import { TierCard } from '@/components/organizations/Detail/components/TierCard';
import { RecentActivityCard } from '@/components/organizations/components/RecentActivityCard';
import { SponsorPagePromotionCard } from '@/components/organizations/components/SponsorPagePromotionCard';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { PlatformContextPreviewLink } from '@/imports/ui/components/context/PlatformContextPreviewLink/PlatformContextPreviewLink';
import { useLinkPreview } from '@/imports/ui/components/context/PlatformContextPreviewLink/useLinkPreview';

import { OverviewContent_OrganizationFragment } from './graphql-types/OverviewContent_OrganizationFragment';

export const ORGANIZATION_QUERY = gql`
  query organizationPageMobileLayout_OrganizationQuery(
    $input: SingleOrganizationInput!
  ) {
    organization(input: $input) {
      result {
        _id
        currentUserOrganizationPermission {
          canManageOrganizationProfile
        }
        isSponsor
        displayName
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        numFollowers
        sponsorship {
          currentTier {
            name
            analyticsTierType
          }
          requestDemoTitle
          sponsorPageAd {
            _id
            title
            body
            registerInterestTitle
            callToAction
            url
            adImage {
              _id
              imageProxy(input: { w: 560 })
            }
          }
        }
        ...OrganizationPageInformation_OrganizationFragment
        ...OrganizationProfileImage_OrganizationFragment
        ...MessageModal_OrganizationFragment
      }
    }
  }
  ${InfoCard.organizationFragment}
  ${OrganizationProfileImage.organizationFragment}
  ${MessageModal.organizationFragment}
`;

export const Content: React.FC<{
  organization: OverviewContent_OrganizationFragment;
}> & {
  organizationFragment: DocumentNode;
} = ({ organization }) => {
  const { data } = useQuery(ORGANIZATION_QUERY, {
    variables: {
      input: {
        id: organization._id,
      },
    },
  });
  const [editorState] = React.useState<EditorState>(
    EditorState.createWithContent(
      organization.aboutUs
        ? convertFromRaw(organization.aboutUs)
        : ContentState.createFromText('')
    )
  );

  const { linkPreview } = useLinkPreview({
    editorState,
    onHidePreviewHandler: () => null,
  });

  return (
    <VStack spacing="4" mb="4">
      {organization.aboutUs &&
      convertFromRaw(organization.aboutUs).hasText() ? (
        <Card w="full" px="4" py="6">
          <Heading size="md" mb="4">
            About Us
          </Heading>

          <Stack spacing="4">
            <Prose>
              <Redraft rawDraftContentState={organization.aboutUs} />
            </Prose>

            <PlatformContextPreviewLink
              linkPreview={linkPreview}
              canEdit={false}
              onClose={() => null}
            />

            <Resources
              organizationId={organization._id}
              alt={organization.displayName}
            />
          </Stack>
        </Card>
      ) : null}

      {data?.organization?.result && (
        <Stack spacing="4" w="full" display={{ base: 'flex', md: 'none' }}>
          <InfoCard organization={data.organization.result} />
          {data.organization.result.sponsorship?.sponsorPageAd && (
            <SponsorPagePromotionCard
              title={data.organization.result.sponsorship.sponsorPageAd.title}
              body={data.organization.result.sponsorship.sponsorPageAd.body}
              callToAction={
                data.organization.result.sponsorship.sponsorPageAd.callToAction
              }
              url={data.organization.result.sponsorship.sponsorPageAd.url}
              image={
                data.organization.result.sponsorship.sponsorPageAd.adImage
                  ?.imageProxy || undefined
              }
              organizationId={data.organization.result._id}
              organizationName={data.organization.result.displayName}
              sponsorTier={
                data.organization.result.sponsorship.currentTier
                  .analyticsTierType
              }
            />
          )}
          {data.organization.result.sponsorship && (
            <TierCard
              organizationName={data.organization.result.displayName}
              tierName={data.organization.result.sponsorship.currentTier.name}
            />
          )}
        </Stack>
      )}

      <RecentActivityCard organization={organization} />
    </VStack>
  );
};
Content.organizationFragment = gql`
  fragment OverviewContent_OrganizationFragment on Organization {
    _id
    aboutUs
    displayName
    ...RecentActivityCard_OrganizationFragment
  }
  ${RecentActivityCard.organizationFragment}
`;
